const $ = (selector, parent = document) => parent.querySelector(selector);
const $$ = (selector, parent = document) => parent.querySelectorAll(selector);

const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "youtu.be",
  "www.youtu.be",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
]);

const VIMEO_HOSTS = new Set([
  "vimeo.com",
  "www.vimeo.com",
  "player.vimeo.com",
]);

function safeURL(value) {
  try {
    return new URL(value, window.location.origin);
  } catch {
    return undefined;
  }
}

function getYouTubeId(url) {
  const parsedURL = safeURL(url);

  if (!parsedURL || !YOUTUBE_HOSTS.has(parsedURL.hostname)) {
    return undefined;
  }

  if (parsedURL.hostname.includes("youtu.be")) {
    const shortID = parsedURL.pathname.replace(/^\//, "").split("/")[0];
    return shortID || undefined;
  }

  const videoID = parsedURL.searchParams.get("v")
    || parsedURL.pathname.match(/\/(embed|shorts|live)\/([^/?#&]+)/)?.[2]
    || parsedURL.pathname.replace(/^\//, "").split("/")[0];

  return videoID || undefined;
}

function getVimeoData(url) {
  const parsedURL = safeURL(url);

  if (!parsedURL || !VIMEO_HOSTS.has(parsedURL.hostname)) {
    return undefined;
  }

  const segments = parsedURL.pathname.split("/").filter(Boolean);
  const numericID = segments.find((segment) => /^\d+$/.test(segment));
  const hash = parsedURL.searchParams.get("h")
    || (segments.length > 0 ? segments[segments.length - 1] : undefined);

  if (!numericID) {
    return undefined;
  }

  return {
    id: numericID,
    hash: hash && hash !== numericID ? hash : undefined,
  };
}

function isYouTubeURL(url) {
  return Boolean(getYouTubeId(url));
}

function isVimeoURL(url) {
  return Boolean(getVimeoData(url));
}

function buildEmbedURL(url) {
  const youTubeID = getYouTubeId(url);

  if (youTubeID) {
    return `https://www.youtube.com/embed/${youTubeID}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
  }

  const vimeoData = getVimeoData(url);

  if (vimeoData) {
    const hashQuery = vimeoData.hash ? `?h=${vimeoData.hash}&` : "?";
    return `https://player.vimeo.com/video/${vimeoData.id}${hashQuery}autoplay=1&title=0&byline=0&portrait=0&dnt=1`;
  }

  return undefined;
}

function createIframe(url) {
  const iframe = document.createElement("iframe");
  iframe.className = "w-full aspect-video";
  iframe.src = url;
  iframe.title = "Embedded video player";
  iframe.setAttribute("frameborder", "0");
  iframe.setAttribute("allow", "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share");
  iframe.setAttribute("allowfullscreen", "");
  return iframe;
}

function createVideo(url) {
  const video = document.createElement("video");
  video.className = "w-full h-auto aspect-video";
  video.controls = true;
  video.autoplay = true;
  video.playsInline = true;
  video.setAttribute("preload", "metadata");

  const source = document.createElement("source");
  source.src = url;

  video.appendChild(source);
  return video;
}

function replaceMediaPlacement(mediaPlacement) {
  const videoURL = mediaPlacement.getAttribute("data-video")?.trim();

  if (!videoURL) {
    return;
  }

  const embedURL = buildEmbedURL(videoURL);
  const replacementNode = embedURL ? createIframe(embedURL) : createVideo(videoURL);

  mediaPlacement.innerHTML = "";
  mediaPlacement.appendChild(replacementNode);
  mediaPlacement.classList.add("is-playing");

  if (!embedURL && replacementNode instanceof HTMLVideoElement) {
    replacementNode.play().catch(() => {
      replacementNode.controls = true;
    });
  }
}

function initializeMediaPlacements() {
  const mediaPlacements = $$(".media-placement");

  mediaPlacements.forEach((mediaPlacement) => {
    if (mediaPlacement.dataset.mediaInitialized === "true") {
      return;
    }

    mediaPlacement.dataset.mediaInitialized = "true";

    const playTrigger = $(".play-button", mediaPlacement);

    if (!playTrigger) {
      return;
    }

    playTrigger.addEventListener("click", (event) => {
      event.preventDefault();
      replaceMediaPlacement(mediaPlacement);
    });

    playTrigger.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        replaceMediaPlacement(mediaPlacement);
      }
    });
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeMediaPlacements);
} else {
  initializeMediaPlacements();
}
